import { createSelector } from "reselect";
import AppState from "../AppState";
import {
  BusinessRulesListDetails,
  ProductId,
  ProductSequenceData,
} from "./ProductListTypes";
import { SEQUENCE_MODE_TYPE } from "../../utils/Constants";
import {
  calculateProgressWithCache,
  calculateV1Progress,
  isProductPublishedSFCC,
} from "../../utils/ProductUtil";
import {
  selectIsFetchedBySearch,
  selectProductColors,
  selectProductIdsBySearch,
  selectProducts,
} from "../product/ProductSelectors";
import { selectIsCategoryProductsV2Available } from "store/adapter-config/AdapterConfigSelectors";
import type { PageFetchStatus } from "./ProductListTypes";

export const selectProductListsFetched = (
  state: AppState,
): ProductSequenceData[] => {
  return state?.productList ? state.productList?.productSequences : [];
};
export const selectProductListEditSequence = (
  state: AppState,
): ProductSequenceData[] => {
  return state.productList ? state.productList.editSequenceList : [];
};
export const selectPublishedProductList = createSelector(
  [selectProductListsFetched, selectProducts],
  (productList, productById) => {
    if (productList) {
      return productList.filter((categoryToProduct) =>
        isProductPublishedSFCC(productById[categoryToProduct.productId]),
      );
    }
    return [];
  },
);

export const selectEditSequenceList = (
  state: AppState,
): ProductSequenceData[] => {
  return state.productList &&
    state.productList.editSequenceList &&
    Array.isArray(state.productList.editSequenceList) &&
    state.productList.editSequenceList.length
    ? state.productList.editSequenceList?.map((sequenceList, sequenceIndex) => {
        return {
          ...sequenceList,
          sequence: sequenceList.sequence,
          isInitialPositionAvailable: sequenceList.sequence ? true : false,
        };
      })
    : [];
};

export const selectPublishedSequenceList = createSelector(
  [selectEditSequenceList, selectProducts],
  (productList, productById) => {
    if (productList) {
      return productList.filter((categoryToProduct) =>
        isProductPublishedSFCC(productById[categoryToProduct.productId]),
      );
    }
    return [];
  },
);
export const selectPublishedSequenceListIds = createSelector(
  [
    selectEditSequenceList,
    selectProductIdsBySearch,
    selectIsFetchedBySearch,
    selectProducts,
  ],
  (productList, productIdsBySearch, isFetchedBySearch, productById) => {
    if (productList.length > 0 && !isFetchedBySearch) {
      return productList
        .filter((categoryToProduct) =>
          isProductPublishedSFCC(productById[categoryToProduct.productId]),
        )
        .map((product) => product.productId);
    } else if (productIdsBySearch.length > 0 && isFetchedBySearch) {
      return productIdsBySearch;
    }
    return [];
  },
);

export const selectIsProductListsFetched = (state: AppState): boolean => {
  return state.productList ? state.productList.isFetched : false;
};

export const selectIsProductListFetching = (state: AppState): boolean => {
  return state.productList ? state.productList.isFetching : false;
};

export const shouldUpdateEditSequenceList = createSelector(
  [selectEditSequenceList, selectProductListsFetched],
  (products, oldProductsState) => {
    // const filteredProducts = products.filter((categoryToProduct, productIndex) =>
    //   isProductPublishedSFCC(categoryToProduct.product)
    // );
    const filteredProducts = products;
    const oldProductSequenceState = oldProductsState;
    let isSaveButtonEnabled =
      Array.isArray(oldProductSequenceState) &&
      oldProductSequenceState.some(
        (product, prdIndex) =>
          product.productId !== filteredProducts?.[prdIndex]?.productId,
      );
    let prevSequence = 0;
    let shouldUpdate = false;
    for (let index = 0; index < filteredProducts.length; index++) {
      if (filteredProducts?.[index]?.sequence && isSaveButtonEnabled) {
        shouldUpdate = true;
        break;
      }
      prevSequence = filteredProducts?.[index]?.sequence || prevSequence;
    }
    return shouldUpdate;
  },
);

export const selectProductSequenceMode = (state: AppState) => {
  return state.productList.sequenceMode === 2;
};

export const selectHasNullSequences = createSelector(
  [selectEditSequenceList, selectProducts],
  (products, productById) => {
    const filteredProducts = products.filter((categoryToProduct) =>
      isProductPublishedSFCC(productById[categoryToProduct.productId]),
    );
    let hasNullSequence = false;
    for (let index = 0; index < filteredProducts.length; index++) {
      if (!filteredProducts?.[index]?.sequence) {
        hasNullSequence = true;
        break;
      }
    }
    return hasNullSequence;
  },
);

export const selectProductListSequenceMode = (state: AppState): number => {
  return state.productList
    ? state.productList.sequenceMode
    : SEQUENCE_MODE_TYPE.VIEW;
};

export const selectProductsWithInvalidSequences = (
  state: AppState,
): string[] => {
  const invalidSequenceProducts: string[] = [];
  if (state.productList) {
    state.productList.productSequences.forEach((product) => {
      if (!product.sequence) invalidSequenceProducts.push(product.productId);
    });
  }
  return invalidSequenceProducts;
};

export const selectMaxSequenceInList = createSelector(
  [selectProductListsFetched],
  (productList) => {
    let maxSequence = 1;
    if (productList) {
      const seqArray = productList.map((product) => product.sequence);
      maxSequence = Math.max(...seqArray.filter((x) => x !== undefined));
    }
    return maxSequence;
  },
);

export const selectNumberOfPinnedProducts = (state: AppState): number => {
  return state.productList && state.productList.pinnedProducts
    ? state.productList.pinnedProducts.numberOfPinnedProducts
    : 0;
};

export const selectPinnedProducts = (state: AppState): string[] => {
  return state.productList && state.productList.pinnedProducts
    ? state.productList.pinnedProducts.pinnedProductIds
    : [];
};

export const shouldUpdateNewPinnedProducts = createSelector(
  [
    selectNumberOfPinnedProducts,
    selectPinnedProducts,
    selectPublishedSequenceList,
    selectIsFetchedBySearch,
  ],
  (
    numberOfPinnedProducts,
    pinnedProducts,
    editSequenceList,
    isFetchedBySearch,
  ) => {
    const newPinnedProducts = editSequenceList
      .map((product) => product.productId)
      .slice(0, numberOfPinnedProducts);
    if (isFetchedBySearch) {
      return false;
    }
    let shouldUpdate = false;
    if (pinnedProducts.length !== numberOfPinnedProducts) {
      shouldUpdate = true;
    } else {
      for (let i = 0; i < newPinnedProducts.length; i++) {
        if (newPinnedProducts[i] !== pinnedProducts[i]) {
          shouldUpdate = true;
          break;
        }
      }
    }
    return shouldUpdate;
  },
);

export const selectIsSequenceUpdated = (state: AppState): boolean => {
  return state.productList && state.productList.isSequenceUpdated;
};

export const selectIsSequenceLoading = (state: AppState): boolean => {
  return state.productList && state.productList.isSequenceLoading;
};

export const selectIsSequenceSaving = (state: AppState): boolean => {
  return state.productList?.isSequenceSaving ?? false;
};

export const selectIsPinnedProductsMode = (state: AppState): boolean => {
  return state.productList && state.productList.isPinnedProductsMode;
};

export const selectSortingRule = (state: AppState): string | null => {
  return state.productList && state.productList.sortingRule;
};

export const getProductSequenceRequestId = (state: AppState): string | null => {
  return state.productList && state.productList.requestId;
};

export const selectSortingRulesList = (
  state: AppState,
): BusinessRulesListDetails[] => {
  return state.productList.sortingRulesList.length
    ? state.productList.sortingRulesList
    : [];
};
export const selectAppliedRulesList = (
  state: AppState,
): BusinessRulesListDetails[] => {
  return state?.businessRulesList?.businessRulesList ?? [];
};

export const selectSortingListLoadedState = (state: AppState): boolean => {
  return state.productList && state.productList.isSortingRulesListLoaded;
};

export const selectIsProductIdsFetched = (state: AppState): boolean => {
  return state.productList && state.productList.isProductIdsFetched;
};

export const selectIsFetchingProductIds = (state: AppState): boolean => {
  return state.productList && state.productList.isFetchingProductIds;
};

export const selectIsFetchingCategoryProductIds = (state: AppState): boolean =>
  state.productList?.isFetchingCategoryProductIds ?? false;

export const selectIsCategoryInitialLoading = (state: AppState): boolean =>
  selectIsFetchingCategoryProductIds(state);

export const selectProductIdsCount = (state: AppState): number => {
  return state.productList && state.productList.productIds.length;
};

export const selectGetProductIds = (state: AppState): ProductId[] => {
  return (state.productList && state.productList.productIds) || [];
};

export const selectProductIds = selectGetProductIds;

export const isRefreshingAllProducts = (state: AppState) => {
  return state.productList && state.productList.isRefreshingAllProducts;
};
export const isFetchingSortedProducts = (state: AppState): boolean => {
  return state.productList && state.productList.isFetchingSortedProducts;
};

export const selectIsUpdateProductSequenceInvoked = (state: AppState) => {
  return state.productList && state.productList.isUpdateFunctionInvoked;
};

export const selectSavingCategoryIds = (state: AppState): string[] => {
  return state.productList?.savingCategoryIds ?? [];
};

export const selectCurrentUserSavingCategoryIds = (state: AppState): string[] => { 
  const allSavingCategoryIds = state.productList?.savingCategoryIds ?? [];
  const currentUserInitiatedSaves = state.productList?.currentUserInitiatedSaves ?? [];
  return allSavingCategoryIds.filter((categoryId) => {
    return currentUserInitiatedSaves.includes(categoryId);
  });
};

export const isAddingRemovingProductIdsList = (state: AppState) => {
  return state.productList && state.productList.addingRemovingProductIdsList;
};

export const selectIsProductSequenceUpdateFailure = (state: AppState) => {
  return state.productList && state.productList.isUpdateProductSequenceError;
};

export const selectWorkingProductSequence = (state: AppState) => {
  return state.productList?.updatedStateProductIds;
};

export const selectNext = (state: AppState) => {
  return state.productList?.productIdsData?.next;
};

export const selectCount = (state: AppState) => {
  return state.productList?.productIdsData?.count;
};

export const selectTotal = (state: AppState) => {
  return state.productList?.productIdsData?.total;
};

export const selectCategoryProductsTotal = (state: AppState): number | undefined =>
  state.productList?.total;

export const selectWatchedProductsList = (state: AppState) => {
  return state.productList && state.productList.watchedProductList;
};

export const selectProgressLoadingPercentage = (
  state: AppState,
  currentPage: number = 1,
  pageSize: number = 24,
): number => {
  const total = state.productList?.total;
  const productIds = state.productList?.updatedStateProductIds;
  const productById = selectProducts(state);
  const colorById = selectProductColors(state);
  const contentSlots = state.productList.contentSlots;
  const isCategoryProductsV2Available =
    selectIsCategoryProductsV2Available(state);

  if (total == null) return 0;
  if (total === 0) return 100;

  // Check if we're still fetching product IDs
  const isFetchingCategoryProductIds =
    state.productList.isFetchingCategoryProductIds;
  const productIdsData = state.productList.productIdsData;
  const hasMoreProductIds = productIdsData?.next;

  // If we're still fetching product IDs and there are more to fetch, calculate progress for product ID fetching (V1 only — V2 shows full page progress regardless of background fetching)
  if (!isCategoryProductsV2Available && isFetchingCategoryProductIds && hasMoreProductIds) {
    const fetchedProductIdsCount = productIds.length;
    const totalProductIdsToFetch = total;

    // Calculate progress for product ID fetching (first 15% of total progress)
    const productIdsProgress = Math.min(
      15,
      Math.floor((fetchedProductIdsCount / totalProductIdsToFetch) * 15),
    );

    return productIdsProgress;
  }

  const contentSlotsInPage =
    contentSlots?.filter(
      (slot) =>
        slot.position > (currentPage - 1) * pageSize &&
        slot.position <= currentPage * pageSize,
    ) || [];

  const availableProductSlots = pageSize - contentSlotsInPage.length;
  const startIndex =
    (currentPage - 1) * pageSize -
      contentSlots?.filter(
        (slot) => slot.position <= (currentPage - 1) * pageSize,
      ).length || 0;
  const endIndex = Math.min(
    startIndex + availableProductSlots,
    productIds.length,
  );

  const pageProductIds = productIds.slice(startIndex, endIndex);
  const cacheKey = `${currentPage}`;

  // If no products on this page, return 100% (nothing to load)
  if (pageProductIds.length === 0) return 100;

  if (isCategoryProductsV2Available) {
    // V2: Need product details + colors (if applicable) + badges (if applicable)
    let totalItemsToLoad = 0;
    let loadedItems = 0;

    for (const productId of pageProductIds) {
      const product = productById[productId];

      // Always need product details
      totalItemsToLoad++;

      // Product details loaded
      if (product !== undefined && product.productDetailsLoaded) {
        loadedItems++;

        // Check if product has colors
        if (product && product.hasColors) {
          totalItemsToLoad++;
          // Colors loaded (if product has colors)
          if (
            colorById[productId] !== undefined &&
            colorById[productId].productColorDetailsLoaded
          ) {
            loadedItems++;
          }
        }
      }
    }

    return calculateProgressWithCache(
      loadedItems,
      totalItemsToLoad,
      cacheKey,
      state.productList?.progressCache,
      isFetchingCategoryProductIds,
      productIds,
      total,
    );
  } else {
    // V1: First page loads all product details (for all pages) + colors for first page, subsequent pages only load colors
    const totalProductsOnPage = pageProductIds.length;

    if (currentPage === 1) {
      // First page: Load all product details (for all pages) + colors for first page only
      const totalProducts = productIds.length; // All products across all pages

      // Count products with colors on first page
      let productsWithColorsCount = 0;
      for (const productId of pageProductIds) {
        const product = productById[productId];
        if (product && product.hasColors) {
          productsWithColorsCount++;
        }
      }

      const totalColorsToLoad = productsWithColorsCount; // Colors only for products that have colors
      const totalItemsToLoad = totalProducts + totalColorsToLoad;

      let loadedItems = 0;

      // Count loaded product details (all products)
      for (const productId of productIds) {
        const product = productById[productId];
        if (product !== undefined) {
          loadedItems++;
        }
      }

      // Count loaded colors (first page only, only for products that have colors)
      for (const productId of pageProductIds) {
        const product = productById[productId];
        if (
          product &&
          product.hasColors &&
          colorById[productId] !== undefined
        ) {
          loadedItems++;
        }
      }

      return calculateV1Progress(
        totalItemsToLoad,
        loadedItems,
        cacheKey,
        state.productList?.progressCache,
      );
    } else {
      // Subsequent pages: Need both product details and colors for this page
      const totalProductsToLoad = totalProductsOnPage;

      // Count products with colors on this page
      let productsWithColorsCount = 0;
      for (const productId of pageProductIds) {
        const product = productById[productId];
        if (product && product.hasColors) {
          productsWithColorsCount++;
        }
      }

      const totalColorsToLoad = productsWithColorsCount; // Colors only for products that have colors
      const totalItemsToLoad = totalProductsToLoad + totalColorsToLoad;

      let loadedItems = 0;

      // Count loaded product details for this page
      for (const productId of pageProductIds) {
        const product = productById[productId];
        if (product !== undefined) {
          loadedItems++;
        }
      }

      // Count loaded colors for this page (only for products that have colors)
      for (const productId of pageProductIds) {
        const product = productById[productId];
        if (
          product &&
          product.hasColors &&
          colorById[productId] !== undefined
        ) {
          loadedItems++;
        }
      }

      return calculateV1Progress(
        totalItemsToLoad,
        loadedItems,
        cacheKey,
        state.productList?.progressCache,
      );
    }
  }
};

export const selectIsSequenceResponseFetched = (state: AppState): boolean => {
  return (
    state.productList &&
    state.productList.productSequenceList?.response?.statusCode === 201
  );
};

export const selectIsSequenceUpdatedRequest = (state: AppState): boolean => {
  return state.productList && state.productList.isSquenceUpdateRequest;
};

export const selectIsSequenceRequestedByBoostBury = (
  state: AppState,
): boolean => {
  return state.productList && state.productList.isBoostBurySequenceRequested;
};

export const selectLockedProductInCurrentCategory = (
  state: AppState,
  categoryId: string,
  catalogId: string,
  productId: string,
  position?: number,
) => {
  if (!state.productList) return false;

  // Get unlocked product IDs for quick lookup
  const unlockedProductIds = new Set(
    state.productList.savedUnlockedProductsInCategory?.map(
      (unlock) => unlock.productId,
    ) ?? [],
  );

  // Check if this product is in the unlocked list
  if (unlockedProductIds.has(productId)) {
    return false;
  }

  // Find the lock if it exists and is not unlocked
  return state.productList.locksInCategory?.find(
    (lock) =>
      lock.categoryId === categoryId &&
      lock.catalogId === catalogId &&
      lock.productId === productId &&
      lock.position === position,
  );
};

export const selectSavedLockedProducts = (state: AppState) => {
  return state.productList && state.productList.savedLockedProductsInCategory;
};

export const selectSavedUnlockedProducts = (state: AppState) => {
  return state.productList && state.productList.savedUnlockedProductsInCategory;
};

export const selectLockedProducts = (
  state: AppState,
  categoryId: string,
  catalogId: string,
) => {
  return (
    state.productList &&
    state.productList.locksInCategory?.filter(
      (lock) => lock.categoryId === categoryId && lock.catalogId === catalogId,
    )
  );
};

export const selectRemovedLockedProductsInCategory = (
  state: AppState,
  categoryId: string,
  catalogId: string,
) => {
  return (
    state.productList &&
    state.productList.locksRemoveInCategory?.filter(
      (lock) => lock.categoryId === categoryId && lock.catalogId === catalogId,
    )
  );
};

export const selectUpdatedLocksInCategory = (
  state: AppState,
  categoryId: string,
  catalogId: string,
) => {
  // Get base locked products for this category/catalog
  const baseLocks =
    state.productList?.locksInCategory?.filter(
      (lock) => lock.categoryId === categoryId && lock.catalogId === catalogId,
    ) ?? [];

  // Get saved locked products
  const savedLocks = state.productList?.savedLockedProductsInCategory ?? [];

  // Get unlocked product IDs for quick lookup
  const unlockedProductIds = new Set(
    state.productList?.savedUnlockedProductsInCategory?.map(
      (unlock) => unlock.productId,
    ) ?? [],
  );

  // Combine and deduplicate using Map for better performance
  const lockMap = new Map();

  // Add base locks first
  baseLocks.forEach((lock) => {
    lockMap.set(lock.productId, lock);
  });

  // Add saved locks (will override base locks if duplicate)
  savedLocks.forEach((lock) => {
    lockMap.set(lock.productId, lock);
  });

  // Convert back to array and filter out unlocked products
  return (
    Array.from(lockMap.values())?.filter(
      (lock) => !unlockedProductIds.has(lock.productId),
    ) ?? []
  );
};

export const selectContentSlots = (state: AppState) => {
  return state.productList && state.productList.contentSlots;
};

export const selectStoredPinnedProductIds = (state: AppState) => {
  return state.productList && state.productList.storedPinnedProductIds;
};

export const selectPinnedProductIds = (state: AppState) => {
  return state.productList && state.productList.pinnedProductIds;
};

export const selectStoredUnpinnedProductIds = (state: AppState) => {
  return state.productList && state.productList.storedUnpinnedProductIds;
};

export const selectIsContentSlotLoading = (state: AppState): boolean => {
  return state.productList?.isContentSlotLoading ?? false;
};

export const selectSelectedItems = (state: AppState): ProductSequenceData[] => {
  return state.productList?.selectedItems ?? [];
};

export const selectCurrentPage = (state: AppState): number => {
  return state.productList?.currentPage ?? 1;
};

export const selectPendingScrollTarget = (
  state: AppState,
): { productId: string; nonce: number } | null => {
  return state.productList?.pendingScrollTarget ?? null;
};

export const selectOriginalProductSequences = (state: AppState) => {
  return state.productList?.originalProductSequences ?? [];
};

export const selectUpdatedPositionedProductIds = (state: AppState) => {
  return state.productList?.updatedPositionedProductIds ?? [];
};

export const selectPreviewOptions = (state: AppState) => {
  return (
    state.productList?.previewOptions ?? {
      date: "",
      stock: false,
      published: false,
    }
  );
};


export const selectIsPreviewMode = (state: AppState): boolean => {
  return state.productList?.isPreviewMode ?? false;
};

export const selectRemainingProductsModal = (state: AppState) => {
  return (
    state.productList?.remainingProductsModal ?? {
      isOpen: false,
      progress: 0,
      total: 0,
      loaded: 0,
      isCancelled: false,
    }
  );
};

export const selectPageFetchStatus = (
  state: AppState,
  page: number,
): PageFetchStatus =>
  (state.productList?.pageFetchStatus?.[page] as PageFetchStatus | undefined) ??
  "idle";

export const selectAllPageFetchStatus = (
  state: AppState,
): Record<number, PageFetchStatus> => state.productList?.pageFetchStatus ?? {};

export const selectPrefetchContextId = (state: AppState): string | null =>
  state.productList?.prefetchContextId ?? null;

export const selectPrefetchPauseUntil = (state: AppState): number =>
  state.productList?.prefetchPauseUntil ?? 0;
